import { useState } from 'react';
import { Box, Typography, IconButton, Collapse, Chip, Tooltip } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import PhoneIcon from '@material-ui/icons/Phone';
import EmailIcon from '@material-ui/icons/Email';
import time from '../../../lib/appfuse-react/core/time';
import { truncateText, MAX_VISIBLE_CHIPS, translateCampaign } from '../utils/helpers';
import { useStyles } from '../styles/useStyles';

/**
 * Grouped duplicate card component
 * Displays a group of duplicate resume entries with expandable details
 */
function DuplicateGroupCard({ group }) {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const { name, email, primaryContactNumber, totalCount, campaignSummary, records } = group;

  return (
    <Box className={classes.groupCard}>
      {/* Header with name and badge */}
      <Box className={classes.groupCardHeader}>
        <Typography className={classes.groupCardName}>{name}</Typography>
        <Box className={classes.groupCardBadge}>{totalCount} 筆重複</Box>
      </Box>

      {/* Phone contact row */}
      {primaryContactNumber && (
        <Box className={classes.contactRow}>
          <PhoneIcon className={classes.contactIcon} />
          <Typography className={classes.contactText}>{primaryContactNumber}</Typography>
        </Box>
      )}

      {/* Email contact row */}
      {email && (
        <Box className={classes.contactRow}>
          <EmailIcon className={classes.contactIcon} />
          <Typography className={classes.contactText}>{email}</Typography>
        </Box>
      )}

      {/* Campaign summary */}
      <Box className={classes.campaignSummaryContainer}>
        <Typography className={classes.campaignSummaryLabel}>重複登記：</Typography>
        <Box className={classes.campaignChipContainer}>
          {campaignSummary && campaignSummary.slice(0, MAX_VISIBLE_CHIPS).map((item, index) => {
            const campaignName = translateCampaign(item.campaign);
            const displayName = truncateText(campaignName, 10);
            const fullLabel = `${campaignName} (${item.count}次)`;
            const truncatedLabel = `${displayName} (${item.count}次)`;
            const needsTooltip = campaignName.length > 10;

            const chip = (
              <Chip
                key={index}
                className={classes.campaignChip}
                label={truncatedLabel}
                size="small"
              />
            );

            return needsTooltip ? (
              <Tooltip key={index} title={fullLabel} arrow placement="top">
                {chip}
              </Tooltip>
            ) : chip;
          })}
          {/* Show "+N more" chip if there are more campaigns */}
          {campaignSummary && campaignSummary.length > MAX_VISIBLE_CHIPS && (
            <Chip
              className={classes.campaignChipMore}
              label={`+${campaignSummary.length - MAX_VISIBLE_CHIPS} 更多`}
              size="small"
              onClick={() => setExpanded(true)}
            />
          )}
        </Box>
      </Box>

      {/* Expand section */}
      <Box className={classes.expandSection} onClick={() => setExpanded(!expanded)}>
        <Typography className={classes.expandLabel}>
          {expanded ? '收合詳細' : '展開詳細'}
        </Typography>
        <IconButton
          className={`${classes.expandButton} ${expanded ? classes.expandButtonOpen : ''}`}
          size="small"
          component="span"
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>

      {/* Expanded records list */}
      <Collapse in={expanded}>
        <Box className={classes.recordList}>
          {records && records.map((record, index) => {
            const campaignName = translateCampaign(record.campaign);
            return (
              <Box key={index} className={classes.recordItem}>
                <Typography className={classes.recordCampaign}>
                  {campaignName}
                </Typography>
                <Typography className={classes.recordTime}>
                  {time.format(record.createdOn, "datetime")}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}

export default DuplicateGroupCard;
